import React from 'react';
import {
  createIntl,
  IntlShape,
  FormattedMessage as BaseFormattedMessage,
  useIntl as baseUseIntl,
  IntlFormatters,
} from 'react-intl';
import { Props as FormattedMessageProps } from 'react-intl/src/components/message';

import messagesEn, { strings } from 'locales/en';

const DEFAULT_LOCALE = 'en';

/**
 * These methods are for typing the locale file.  Some of this was inspired
 * from this post: https://gist.github.com/cpv123/afccb1a87d235b9300547f00180c3b99
 */

/**
 * Allows for a Record type to only have a subset of the keys passed in
 */
type PartialRecord<K extends keyof any, T> = {
  [P in K]?: T;
};

/**
 * Concats the keys of a object tree with a `.`.  Form example
 *
 * const tree = {
 *   coaching: {
 *     buttons: {
 *       done: 'Done',
 *       ok: 'ok,
 *     },
 *     withCoach: 'with {coachName}'
 *   }
 * }
 *
 * would have 3 keys where the value of ConcatKeys<typeof tree> would be:
 *  `'coaching.buttons.done' | 'coaching.buttons.ok' | 'coaching.withCoach'`
 *
 * Much of this was taken from this stack-overflow post:
 *   https://stackoverflow.com/questions/58434389/typescript-deep-keyof-of-a-nested-object/68404823#68404823
 */
type ConcatKeys<T> = (
  T extends object
    ? {
        [K in Exclude<keyof T, symbol>]: ConcatKeys<T[K]> extends ''
          ? K
          : `${K}.${ConcatKeys<T[K]>}`;
      }[Exclude<keyof T, symbol>]
    : ''
) extends infer D
  ? Extract<D, string>
  : never;

/**
 * Sets the type of all our flatted keys in the English locale file
 */
export type IntlMessageKeys = ConcatKeys<typeof strings>;

/**
 * Works like the dig method in ruby in that if you pass it a concatinated key like
 * 'coaching.buttons.done' it will return the 'Done' value much like doing
 * tree['coaching']['buttons]['done']
 */
type Dig<CK extends string, T> = CK extends `${infer K}.${infer V}`
  ? K extends keyof T
    ? Dig<V, T[K]>
    : never
  : T extends Record<CK, infer V2>
  ? V2
  : never;

/**
 * Pulls out any non-plural tokens inside a substring that contains plural tokens.
 * Because we can't do regexes in Typescript type inference, we can't properly match
 * to a plural token {count, plural, =0 {no items} one {1 item} other {# items}}.
 * In the case where a string has both regular tokens and plural-tokens we need to
 * pull out any regular tokens before we get the plural token.
 *
 * `{coachName} has {count, plural, one {1 student} other {# students}} today`
 *
 * In string above, we'd skip count if we didn't use this inferrence to get
 * `coachName` first and then `count`.
 */
type InternalPluralToken<T> = T extends `${infer Token}}${any}{${infer Rest}`
  ? Token | InternalPluralToken<Rest>
  : T;

/**
 * Extracts all the value tokens from a string.  We use this to determine
 * What values should be possible for which translation key.
 */
type ExtractTokens<T> = T extends `${any}{${infer Plural},${any}}}${infer Rest}`
  ? InternalPluralToken<Plural> | ExtractTokens<Rest>
  : T extends `${any}{${infer Token}}${infer Rest}`
  ? Token | ExtractTokens<Rest>
  : never;

/**
 * All possible tokens in the our locale file
 */
export type ValueTokens = ExtractTokens<Dig<IntlMessageKeys, typeof strings>>;

/**
 * Makes a giantic union type of translation keys and their possible tokens
 * for the FormattedMessage component's type signature.
 *
 * Note: we need the final unioned type to handle when keys are passed in
 * as union type.
 */
export type FormattedMessageTypedProps =
  | (FormattedMessageProps &
      {
        [K in IntlMessageKeys]: {
          id: K;
          values?: Record<ExtractTokens<Dig<K, typeof strings>>, any>;
        };
      }[IntlMessageKeys])
  | (FormattedMessageProps & {
      id: IntlMessageKeys;
      values?: PartialRecord<ValueTokens, any>;
    });

/**
 * This is the current arguments for the formatMessage method of the intl object.
 */
type FormatMessageProps = Parameters<IntlFormatters['formatMessage']>;

/**
 * Makes a giantic union type of translation keys and their possible tokens
 * for the formatMessage method's signature of intl object
 */
export type FormatMessageTypedProps =
  | {
      [K in IntlMessageKeys]: [
        descriptor: FormatMessageProps[0] & { id: K },
        values?: Record<ExtractTokens<Dig<K, typeof strings>>, any>,
        opts?: FormatMessageProps[2]
      ];
    }[IntlMessageKeys]
  | [
      descriptor: FormatMessageProps[0] & { id: IntlMessageKeys },
      values?: PartialRecord<ValueTokens, any>,
      opts?: FormatMessageProps[2]
    ];

/**
 * This is the current type signature for the formatMessage method of the intl object.
 */
export type FormatMessageFn = (...args: FormatMessageTypedProps) => string;

/**
 * In case someone uses the react-intl versions of FormattedMessage and the
 * intl object's formatMessage method at least we can have the ids be type-checked.
 * The values could not be type-checked with the ids as that requires a unioned
 * mapped type and the current FormatMessage component uses an interface which
 * doesn't support unions or mapped types.
 *
 * For now, however, there are a number of FormattedMessages that are not yet in
 * the locale file, so we're disabling this
 * TODO: put all missing FormattedMessage keys in locale file.
 */
// declare global {
//   namespace FormatjsIntl {
//     interface Message {
//       ids: IntlMessageKeys;
//     }
//   }
// }

/**
 * Adds more strict type-checking to the FormattedMessage component for both
 * the id attribute and the values attribute
 */
export const FormattedMessage: React.FC<FormattedMessageTypedProps> = (props) => {
  return <BaseFormattedMessage {...props} />;
};

/**
 * Adds more strict type-checking to the formatMessage method of the intl
 * object for both the id attribute and the values argument.
 */
export const useIntl = () => {
  const intl = baseUseIntl();

  const baseFormatMessage = intl.formatMessage;

  const formatMessage = (...args: FormatMessageTypedProps) => {
    return baseFormatMessage(...(args as FormatMessageProps));
  };

  return {
    ...intl,
    formatMessage,
  };
};

let _intl: IntlShape;
/**
 * Gets/creates a separate intl object if we need to do localization
 * outside of a React component.
 */
export const getIntl = () => {
  if (!_intl) {
    const { locale, messages } = getMessages();
    _intl = createIntl({ locale, messages });
  }

  return _intl;
};

/**
 * Returns the locale and message object based on the user's
 * browser's locale.  If we don't have messages for that locale
 * it defaults to English.
 *
 * At some point, we might want to check user preferences rather
 * than just the browser locale.
 */
export const getMessages = () => {
  // We might get this from user preferences as well
  let locale = navigator.language;

  const messages: Record<string, typeof messagesEn> = {
    en: messagesEn,
  };

  if (!messages[locale]) {
    locale = DEFAULT_LOCALE;
  }

  return { locale, messages: messages[locale] };
};

/**
 * Uses https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl/ListFormat
 * to format an array of strings into a localized list
 */
export const formatList = (list: string[]) => {
  const intl = baseUseIntl();
  return intl.formatList(list, { style: 'long', type: 'conjunction' });
};
